import {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'

import { Alert, AlertIndicator, Box, Button, Drawer, Tabs, useDisclosure } from '@chakra-ui/react'
import { MODAL_TYPES } from '@editor/config/constants'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { ErrorResponse } from '@editor/interfaces/errors'
import { ProjectSheetItemsOrder } from '@editor/interfaces/project'
import { COMMON_MESSAGE } from '@editor/services/ErrorHandler'
import { updateInspectionAreas } from '@editor/services/InspectionArea'
import { patchInspectionSheets as patchInspectionSheetsAPI } from '@editor/services/InspectionSheet'
import { updateProject } from '@editor/services/Projects'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { fetchInspectionAreas, patchInspectionAreas, setProject } from '@editor/store/page'
import axios from 'axios'
import { isEqual } from 'lodash'

import {
  SettingDrawerTab,
  patchInspectionSheets,
  patchSettings,
  resetEditedInspectionAreas,
  resetEditedInspectionSheetIds,
  setError,
  setHasConflictedAreaName,
  setInspectionAreaIdToEdit,
  setSavingSettings as setIsSheetSaving,
  setSettingDrawerTab,
  setSettings,
} from '../../store'
import SettingsDrawerAreaList from './Tabs/AreaList'
import SettingsDrawerData from './Tabs/Data'

const AllTabs = [
  {
    key: SettingDrawerTab.Data,
    title: 'データ',
    content: SettingsDrawerData,
  },
  {
    key: SettingDrawerTab.Edit,
    title: 'エリア一覧',
    content: SettingsDrawerAreaList,
  },
]

const SettingsDrawerFunction: ForwardRefRenderFunction<{
  openDrawer: () => void
}> = (props, ref) => {
  const { open, onOpen, onClose } = useDisclosure()

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showModal, showErrorModal } = useContext(GlobalModalContext)

  // Stores
  const dispatch = useAppDispatch()
  const project = useAppSelector((state) => state.page.project)
  const settings = useAppSelector((state) => state.inspectionSheet.settings)
  const inspectionAreaIdToEdit = useAppSelector((state) => state.inspectionSheet.inspectionAreaIdToEdit)
  const editedInspectionSheets = useAppSelector((state) => state.inspectionSheet.editedInspectionSheets)
  const editedInspectionAreas = useAppSelector((state) => state.inspectionSheet.editedInspectionAreas)
  const errorMessage = useAppSelector((state) => state.inspectionSheet.errorMessage)
  const settingDrawerTab = useAppSelector((state) => state.inspectionSheet.settingDrawerTab)
  const hasConflictedAreaName = useAppSelector((state) => state.inspectionSheet.hasConflictedAreaName)
  const isSavingSettings = useAppSelector((state) => state.inspectionSheet.isSavingSettings)

  // Refs
  const drawerBody = useRef<HTMLDivElement>(null)

  /**
   * Merge the project settings with the default settings
   */
  useEffect(() => {
    if (project) {
      dispatch(patchSettings(project))
    }
  }, [project, dispatch])

  /**
   * Close the drawer
   */
  const handleClose = useCallback(() => {
    dispatch(setInspectionAreaIdToEdit(null))
    dispatch(setError(''))
    onClose()
  }, [dispatch, onClose])

  /**
   * Reset data.
   */
  const handleReset = useCallback(() => {
    dispatch(resetEditedInspectionAreas())
    dispatch(resetEditedInspectionSheetIds())
    dispatch(setSettingDrawerTab(SettingDrawerTab.Data))
    dispatch(setHasConflictedAreaName(false))
    if (project) {
      dispatch(setSettings(project))
    }
  }, [dispatch, project])

  /**
   * Cancel the changes
   */
  const handleCancel = useCallback(() => {
    if (editedInspectionSheets.length || editedInspectionAreas.length || !isEqual(settings, project)) {
      showModal({
        modalType: MODAL_TYPES.CONFIRMATION_CRITICAL,
        title: '変更を破棄します',
        body: '変更内容が失われますが、よろしいですか？',
        confirmText: '破棄',
        onConfirm: () => {
          handleClose()
          handleReset()
          return true
        },
      })
    } else {
      handleClose()
      handleReset()
    }
  }, [
    handleClose,
    handleReset,
    showModal,
    editedInspectionSheets.length,
    editedInspectionAreas.length,
    settings,
    project,
  ])

  /**
   * Save the settings
   */
  const handleSave = useCallback(async () => {
    if (!project) {
      return
    }

    // Get access token
    const accessToken = await getAccessToken()
    if (!accessToken) {
      dispatch(setIsSheetSaving(false))
      return
    }

    // immediately close the drawer and show the loading spinner
    dispatch(setIsSheetSaving(true))
    handleClose()

    // Reset some values depending on settings.
    // If grouping is not used but order type is set to CustomOrder, reset the order type to CreatedAt
    const updatedSettings = { ...settings }
    if (
      !updatedSettings.sheet_use_inspection_group_order &&
      updatedSettings.sheet_order_type_of_items === ProjectSheetItemsOrder.CustomOrder
    ) {
      updatedSettings.sheet_order_type_of_items = ProjectSheetItemsOrder.CreatedAt
    }

    // (the auto formatter makes it super hard to read instead)
    // prettier-ignore
    const results = await Promise.allSettled([
      updateProject(accessToken, { ...project, ...updatedSettings }),
      editedInspectionSheets.length ? patchInspectionSheetsAPI(accessToken, project.project_id, editedInspectionSheets) : Promise.resolve([]),
      editedInspectionAreas.length ? updateInspectionAreas(accessToken, project.project_id, editedInspectionAreas) : Promise.resolve([])
    ])

    // Save changed values
    if (results[0]?.status === 'fulfilled' && results[0].value) {
      dispatch(setSettings(results[0].value))
      dispatch(setProject(results[0].value))
    }

    if (results[1]?.status === 'fulfilled' && results[1].value) {
      dispatch(patchInspectionSheets(results[1].value))
    }

    // inspection areas has custom error handling due to possible name conflicts
    if (
      results[2]?.status === 'rejected' &&
      axios.isAxiosError(results[2].reason) &&
      (results[2].reason.response?.data as ErrorResponse)?.status_id === 40011
    ) {
      // re-fetch the inspection areas to show the user the conflicting names
      await dispatch(
        fetchInspectionAreas({ access_token: accessToken, project_id: project.project_id, showErrorModal }),
      )

      dispatch(setSettingDrawerTab(SettingDrawerTab.Edit))
    } else if (results[2]?.status === 'fulfilled' && results[2].value) {
      dispatch(patchInspectionAreas(results[2].value))
    }

    // if even one of the requests failed, show an error message, and re-open the drawer
    if (results.some((result) => result.status === 'rejected')) {
      dispatch(setError(COMMON_MESSAGE))
      onOpen()
    } else {
      handleReset()
    }

    dispatch(setIsSheetSaving(false))
  }, [
    settings,
    project,
    editedInspectionAreas,
    editedInspectionSheets,
    dispatch,
    getAccessToken,
    showErrorModal,
    handleClose,
    onOpen,
    handleReset,
  ])

  /**
   * Scroll to the edit area
   */
  const scrollToEditArea = useCallback(() => {
    const box = document.getElementById(`edit-inspection-area-${inspectionAreaIdToEdit}`)

    if (!box) {
      setTimeout(scrollToEditArea, 100)
      return
    }

    drawerBody.current?.scrollTo({ top: box?.offsetTop })
  }, [inspectionAreaIdToEdit])

  /**
   * Open the drawer
   */
  const openDrawer = useCallback(() => {
    onOpen()

    if (inspectionAreaIdToEdit) {
      scrollToEditArea()
    }
  }, [onOpen, scrollToEditArea, inspectionAreaIdToEdit])

  useImperativeHandle(ref, () => ({ openDrawer }))

  return (
    <Drawer.Root
      open={open}
      placement="end"
      onOpenChange={(e) => {
        if (!e.open) handleClose()
      }}
      size="sm"
      closeOnEscape={false}
      closeOnInteractOutside={false}
    >
      <Drawer.Backdrop bg="blackAlpha.300" />
      <Drawer.Positioner>
        <Drawer.Content>
          <Drawer.Header textAlign="center" p={0}>
            <Box
              fontSize="xl"
              fontWeight="semibold"
              color="gray2.700"
              borderBottomColor="gray2.300"
              borderBottomWidth="1px"
              p={6}
              w="full"
            >
              検査帳票の設定
            </Box>
          </Drawer.Header>

          {open && (
            <Tabs.Root defaultValue={settingDrawerTab} overflow="hidden" flex={1} display="flex" flexDirection="column">
              {/* Tab list */}
              <Tabs.List pt={4} mx={4}>
                {AllTabs.map((tab) => (
                  <Tabs.Trigger key={tab.key} value={tab.key}>
                    {tab.title}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>

              {errorMessage && (
                <Alert.Root status="error">
                  <AlertIndicator />
                  {errorMessage}
                </Alert.Root>
              )}

              {/* Tab contents */}
              <Drawer.Body pb={4} ref={drawerBody} overflow="auto" height="full" flex={1}>
                {AllTabs.map((tab) => (
                  <Tabs.Content key={tab.key} value={tab.key} pt={4}>
                    <tab.content />
                  </Tabs.Content>
                ))}
              </Drawer.Body>
            </Tabs.Root>
          )}

          <Drawer.Footer borderTopColor="gray2.300" borderTopWidth="1px" p={3} px={4}>
            <Button variant="ghost" mr={3} onClick={handleCancel} disabled={isSavingSettings}>
              キャンセル
            </Button>
            <Button
              colorPalette="blue"
              onClick={handleSave}
              disabled={hasConflictedAreaName || isSavingSettings}
              loading={isSavingSettings}
              loadingText="保存中..."
            >
              保存
            </Button>
          </Drawer.Footer>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  )
}

// Note: forwardRef is from react, not chakra-ui
const SettingsDrawer = forwardRef(SettingsDrawerFunction)
export default SettingsDrawer
